import React, { useState, useRef, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FaUserCircle, FaAddressBook } from "react-icons/fa"; // Added FaAddressBook for AccountsContact
import { MdOutlineDashboard } from "react-icons/md";
import { IoNewspaperSharp } from "react-icons/io5";
import Logo from "../../assets/LOGO_WHITE.svg";
import { HiHomeModern } from "react-icons/hi2";
import { FaPeopleRoof } from "react-icons/fa6";
import { MdQuestionAnswer } from "react-icons/md";

import { ADMIN_BASE } from "../../config";

const menuItems = [
  {
    Icon: MdOutlineDashboard,
    label: "Dashboard",
    href: `${ADMIN_BASE}/dashboard`,
  },
  {
    Icon: HiHomeModern,
    label: "Residence",
    href: `${ADMIN_BASE}/residences`,
  },
  {
    Icon: FaPeopleRoof,
    label: "Residents",
    href: `${ADMIN_BASE}/residents`,
  },
  {
    Icon: MdQuestionAnswer,
    label: "Requests",
    href: `${ADMIN_BASE}/requests`,
  },
  {
    Icon: IoNewspaperSharp,
    label: "News",
    href: `${ADMIN_BASE}/news`,
  },
  {
    Icon: FaAddressBook,
    label: "Contacts",
    href: `${ADMIN_BASE}/contact-info`,
  },
];

const SideBar = () => {
  const [showLogoutCard, setShowLogoutCard] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [user, setUser] = useState(null);
  const logoutCardRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
  }, []);

  // Close logout card when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        logoutCardRef.current &&
        !logoutCardRef.current.contains(event.target) &&
        !event.target.closest("#logout-toggle-button") &&
        !event.target.closest("#logout-toggle-button-mobile")
      ) {
        setShowLogoutCard(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleLogoutCard = () => setShowLogoutCard((prev) => !prev);

  const handleLogout = () => {
    localStorage.removeItem("user");
    localStorage.removeItem("token");
    setUser(null);
    setShowLogoutCard(false);
    navigate("/admin/login");
  };

  return (
    <>
      {/* Desktop Sidebar */}
      <nav className="hidden lg:flex h-screen sticky top-0 shadow-md p-2 flex-col bg-Dark text-white w-16 z-[9999]">
        {/* Logo */}
        <div className="mb-0 mt-8 w-full flex justify-center">
          <img src={Logo} alt="Dashboard Logo" className="h-10 w-auto" />
        </div>

        {/* Menu Items */}
        <div className="flex-1 mt-10">
          <ul>
            {menuItems.map(({ Icon, label, href }, index) => (
              <li
                key={index}
                className="py-0 my-2 rounded-md font-Roboto duration-300 flex gap-2 items-center relative group"
              >
                <NavLink
                  to={href}
                  className={({ isActive }) =>
                    isActive
                      ? "bg-DarkOfDark px-2 py-2 my-2 rounded-sm flex gap-2 items-center border-Light border-l-4"
                      : "hover:scale-100 px-3 py-2 my-2 flex gap-2 items-center"
                  }
                >
                  <Icon size={30} />
                </NavLink>
                <p className="absolute left-16 z-50 whitespace-nowrap bg-white text-black shadow-md rounded-md px-2 py-1 opacity-0 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:scale-105">
                  {label}
                </p>
              </li>
            ))}
          </ul>
        </div>

        {/* Profile Icon (toggle logout card) */}
        <button
          id="logout-toggle-button"
          onClick={toggleLogoutCard}
          className="absolute bottom-5 left-1/2 transform -translate-x-1/2"
          aria-label="Toggle Profile Card"
        >
          <FaUserCircle size={30} />
        </button>
      </nav>

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 bg-Dark text-white flex items-center justify-between px-6 py-3 z-[9999] shadow-inner">
        {/* Hamburger Icon */}
        <button
          onClick={() => setShowMobileMenu(true)}
          className="text-white"
          aria-label="Open Menu"
        >
          <svg
            className="w-7 h-7"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 6h16M4 12h16M4 18h16"
            />
          </svg>
        </button>

        {/* Spacer */}
        <div />

        {/* Profile Icon inline on bottom bar right */}
        <button
          id="logout-toggle-button-mobile"
          onClick={toggleLogoutCard}
          className="text-white"
          aria-label="Profile"
        >
          <FaUserCircle size={32} />
        </button>
      </nav>

      {/* Mobile Slide-in Sidebar for menu */}
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-Dark text-white shadow-lg z-[9999] transform transition-transform duration-300 ease-in-out
    ${showMobileMenu ? "translate-x-0" : "-translate-x-full"}
  `}
      >
        <div className="p-4 flex justify-between items-center border-b border-gray-700">
          <img src={Logo} alt="Dashboard Logo" className="h-10 w-auto" />
          <button
            onClick={() => setShowMobileMenu(false)}
            className="text-white text-3xl font-bold"
            aria-label="Close Menu"
          >
            &times;
          </button>
        </div>
        <ul className="mt-6">
          {menuItems.map(({ Icon, label, href }, index) => (
            <li key={index}>
              <NavLink
                to={href}
                onClick={() => setShowMobileMenu(false)}
                className={({ isActive }) =>
                  isActive
                    ? "flex items-center gap-3 px-6 py-3 bg-DarkOfDark border-l-4 border-Light"
                    : "flex items-center gap-3 px-6 py-3 hover:bg-DarkOfDark"
                }
              >
                <Icon size={24} />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      {/* Overlay when mobile menu is open */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[9998]"
          onClick={() => setShowMobileMenu(false)}
          aria-hidden="true"
        />
      )}

      {/* Profile / Logout Card */}
      {showLogoutCard && (
        <div
          ref={logoutCardRef}
          className="fixed bottom-20 right-4 font-Poppins lg:left-20 lg:bottom-5 lg:right-auto bg-white p-4 rounded-lg shadow-xl z-[10000] max-w-xs w-72"
          role="dialog"
          aria-modal="true"
          aria-labelledby="profile-card-title"
        >
          <div className="flex items-center gap-3 mb-3">
            <FaUserCircle size={32} className="text-Dark" />
            <div>
              <p
                id="profile-card-title"
                className="text-lg font-semibold text-Dark"
              >
                {user ? user.username : "Admin User"}
              </p>
              <p className="text-sm text-gray-600">
                {user ? user.email : "admin@example.com"}
              </p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="bg-Dark text-white px-4 py-2 rounded-md hover:bg-Hover w-full"
          >
            Logout
          </button>
        </div>
      )}
    </>
  );
};

export default SideBar;
